/* ============================================================
   <mps-included-drawer> — bottom sheet (mobile) / modal (desktop)
   for the "What's included" items inside the Option Cards (full)
   block of main-product-section. Mounted once per page.
   ============================================================ */

class MpsIncludedDrawer extends HTMLElement {
  connectedCallback() {
    /* Shell dedup — both main-product-section (when option_cards_full
       is present) and pdp-option-cards (the standalone section) render
       the drawer shell. If multiple shells land on the same page we
       keep the first as the active drawer and quietly disable the rest
       so triggers don't fire on a hidden duplicate. */
    if (window.__mpsIpdActive) {
      this.setAttribute('hidden', '');
      this.dataset.mpsIpdInert = '1';
      return;
    }
    window.__mpsIpdActive = this;

    this.contentEl   = this.querySelector('[data-mps-ipd-content]');
    this.scrollEl    = this.querySelector('[data-mps-ipd-scroll]');
    this.backdropEl  = this.querySelector('[data-mps-ipd-backdrop]');
    this.viewLinkEl  = this.querySelector('[data-mps-ipd-view]');
    this.atcLabelEl  = this.querySelector('[data-mps-ipd-atc-label]');
    this.variantInputEl = this.querySelector('[data-mps-ipd-variant-input]');

    this._lastFocusedTrigger = null;

    this._bindGlobalTriggers();
    this._bindCloseInteractions();
    this._bindCartChangeClose();
  }

  /* ---- Open from any [data-mps-ipd-trigger] anywhere on the page ---- */
  _bindGlobalTriggers() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-mps-ipd-trigger]');
      if (!trigger) return;
      e.preventDefault();
      this.open(trigger);
    });

    /* Triggers are non-<button> elements (role="button"), so replicate native
       button keyboard activation: Enter/Space open the drawer. */
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const trigger = e.target.closest('[data-mps-ipd-trigger]');
      if (!trigger) return;
      e.preventDefault();
      this.open(trigger);
    });
  }

  _bindCloseInteractions() {
    this.querySelectorAll('[data-mps-ipd-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });
    this.backdropEl.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  /* ---- Open ---- */
  open(trigger) {
    const tplKey = trigger.dataset.mpsIpdTplKey;
    const tpl = document.querySelector(`template[data-mps-ipd-template][data-mps-ipd-tpl-key="${CSS.escape(tplKey)}"]`);
    if (!tpl) {
      console.warn('[mps-ipd] template not found for', tplKey);
      return;
    }

    this._lastFocusedTrigger = trigger;

    /* Inject pre-rendered content (cloned from <template>, no innerHTML construction). */
    this.contentEl.replaceChildren(tpl.content.cloneNode(true));

    /* Wire CTA bar from template data attrs. The Alpine xProductCart pipeline
       reads the variant id from the form's `name="id"` input, so updating it
       here is enough — addToCart picks it up via FormData on click. */
    if (this.variantInputEl) this.variantInputEl.value = tpl.dataset.variantId || '';
    if (this.viewLinkEl) this.viewLinkEl.href = tpl.dataset.productUrl || '#';
    if (this.atcLabelEl) this.atcLabelEl.textContent = tpl.dataset.atcLabel || 'Add to Cart';

    /* Reset scrolling + carousel progress. */
    if (this.scrollEl) this.scrollEl.scrollTop = 0;
    this._initSliderScrollBar();
    this._initVariantPicker();
    this._initQuantityPicker();

    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    /* Force a reflow so the transition kicks in from the off-screen state. */
    void this.offsetWidth;
    this.classList.add('is-open');

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    /* Move focus into the sheet for screen readers / keyboard users. */
    requestAnimationFrame(() => {
      const closeBtn = this.querySelector('[data-mps-ipd-close]');
      if (closeBtn) closeBtn.focus();
    });
  }

  /* ---- Close ---- */
  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';

    /* Wait for the sheet transition before fully hiding so screen readers
       don't see a flash of empty content. */
    const finish = () => {
      this.setAttribute('hidden', '');
      this.contentEl.replaceChildren();
      if (this._lastFocusedTrigger) {
        try { this._lastFocusedTrigger.focus(); } catch (_) {}
        this._lastFocusedTrigger = null;
      }
    };

    const sheet = this.querySelector('.mps-ipd__sheet');
    if (sheet) {
      let done = false;
      const onEnd = () => { if (done) return; done = true; sheet.removeEventListener('transitionend', onEnd); finish(); };
      sheet.addEventListener('transitionend', onEnd);
      /* Fallback in case transitionend doesn't fire (e.g. reduced motion). */
      setTimeout(onEnd, 360);
    } else {
      finish();
    }
  }

  /* ---- Carousel scroll-bar (mirrors main-product-section pattern) ---- */
  _initSliderScrollBar() {
    const slider = this.contentEl.querySelector('[data-mps-ipd-slider]');
    const bar = this.contentEl.querySelector('[data-mps-ipd-scroll-bar]');
    if (!slider || !bar) return;

    const update = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      if (maxScroll <= 0) {
        bar.style.transform = 'translateX(0)';
        return;
      }
      const pct = slider.scrollLeft / maxScroll;
      const trackWidth = bar.parentElement.clientWidth;
      bar.style.transform = `translateX(${pct * (trackWidth - 15)}px)`;
    };

    slider.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ---- Variant (size) picker ----
     Each <li data-mps-ipd-variant> carries the variant's pre-formatted
     price strings as data attributes (Liquid does the formatting; JS
     never formats prices). Clicking an option:
       1. Updates the form's hidden `id` input → Alpine ATC picks it up
       2. Swaps the price block strings in place
       3. Updates the size-picker chip + label */
  _initVariantPicker() {
    const root = this.contentEl.querySelector('[data-mps-ipd-select]');
    if (!root) return;

    const toggleBtn = root.querySelector('[data-mps-ipd-select-toggle]');
    const menu = root.querySelector('[data-mps-ipd-select-menu]');
    const valueEl = root.querySelector('[data-mps-ipd-select-value]');

    const closeMenu = () => {
      menu.hidden = true;
      root.classList.remove('is-open');
      toggleBtn.setAttribute('aria-expanded', 'false');
    };
    const openMenu = () => {
      menu.hidden = false;
      root.classList.add('is-open');
      toggleBtn.setAttribute('aria-expanded', 'true');
    };

    toggleBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      menu.hidden ? openMenu() : closeMenu();
    });
    document.addEventListener('click', (e) => {
      if (root.contains(e.target)) return;
      closeMenu();
    });

    root.querySelectorAll('[data-mps-ipd-variant]').forEach((opt) => {
      opt.addEventListener('click', () => {
        const variantId = opt.dataset.variantId;
        const title = opt.dataset.variantTitle;
        const price = opt.dataset.variantPriceFormatted;
        const compare = opt.dataset.variantCompareFormatted;
        const pct = opt.dataset.variantPct;

        if (this.variantInputEl) this.variantInputEl.value = variantId;
        if (valueEl) valueEl.textContent = title;

        const saleEl = this.contentEl.querySelector('[data-mps-ipd-price-sale]');
        const compareEl = this.contentEl.querySelector('[data-mps-ipd-price-compare]');
        const pctEl = this.contentEl.querySelector('[data-mps-ipd-price-pct]');
        if (saleEl) saleEl.textContent = price;
        if (compareEl) {
          if (compare) {
            compareEl.textContent = compare;
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
        if (pctEl) {
          if (pct && parseInt(pct, 10) > 0) {
            pctEl.textContent = pct + '% Off';
            pctEl.hidden = false;
          } else {
            pctEl.hidden = true;
          }
        }

        if (this.atcLabelEl) this.atcLabelEl.textContent = 'Add to Cart - ' + price;

        root.querySelectorAll('[data-mps-ipd-variant]').forEach((o) => {
          const isActive = o === opt;
          o.classList.toggle('is-active', isActive);
          o.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        closeMenu();
      });
    });
  }

  /* ---- Hydrate the qty mount placeholder ----
     Items rendered by pdp-option-cards leave an empty mount in the
     drawer body. We clone main-product-section's quantity-options DOM,
     rename main-page classes to drawer-scoped ones (so the drawer's CSS
     applies), and inject. If main-product-section isn't on the page,
     the mount stays empty — the form's quantity stays at 1. */
  _hydrateQtyMount() {
    const mount = this.contentEl.querySelector('[data-mps-ipd-qty-mount]');
    if (!mount) return;

    const source = document.querySelector('main-product-section [data-qty-options]');
    if (!source) {
      mount.hidden = true;
      return;
    }

    /* Pull the qty-picker label text from main-product-section's Liquid. */
    const labelSrc = document.querySelector('main-product-section [data-qty-options]')
      ?.closest('.section-mps__qty-group')
      ?.querySelector('.section-mps__selector-label');
    const labelText = (labelSrc && labelSrc.textContent.trim()) || 'Quantity:';

    const labelEl = document.createElement('p');
    labelEl.className = 'mps-ipd__field-label';
    labelEl.textContent = labelText;

    const buttonsContainer = source.cloneNode(true);
    buttonsContainer.className = 'mps-ipd__qty-buttons';
    buttonsContainer.removeAttribute('data-qty-options');
    buttonsContainer.setAttribute('role', 'radiogroup');
    buttonsContainer.setAttribute('aria-label', labelText);

    /* Rename mps-page classes to drawer-scoped equivalents and rebrand
       the data hooks so our existing click bindings pick them up. */
    buttonsContainer.querySelectorAll('.section-mps__qty-btn').forEach((btn) => {
      const isActive = btn.classList.contains('section-mps__qty-btn--active');
      btn.className = 'mps-ipd__qty-btn' + (isActive ? ' mps-ipd__qty-btn--active' : '');
      btn.removeAttribute('data-qty-btn');
      btn.setAttribute('data-mps-ipd-qty-btn', '');
    });
    buttonsContainer.querySelectorAll('.section-mps__qty-num').forEach((el) => {
      el.className = 'mps-ipd__qty-num';
    });
    buttonsContainer.querySelectorAll('.section-mps__qty-discount').forEach((el) => {
      el.className = 'mps-ipd__qty-discount';
    });

    mount.replaceChildren(labelEl, buttonsContainer);
    mount.hidden = false;
  }

  /* ---- Quantity tier buttons ----
     Mirrors main-product-section's quantity picker. The buttons are
     server-rendered from the same `quantity_option` blocks, so labels +
     discount badges stay in sync with whatever the merchant configured
     in the customizer. Active button writes its `data-qty` into the
     form's hidden `quantity` input; Alpine xProductCart picks it up via
     FormData.

     For items rendered by the standalone pdp-option-cards section, the
     server-side template can't introspect main-product-section's blocks
     at render time, so it leaves a [data-mps-ipd-qty-mount] placeholder.
     Here we hydrate that mount by cloning main-product-section's qty
     buttons at open time — single source of truth. */
  _initQuantityPicker() {
    this._hydrateQtyMount();
    const btns = this.contentEl.querySelectorAll('[data-mps-ipd-qty-btn]');
    const qtyInput = this.querySelector('[data-mps-ipd-quantity-input]');
    if (!btns.length) {
      if (qtyInput) qtyInput.value = '1';
      return;
    }

    /* Seed the form input from whichever button is rendered active
       (Liquid marks `forloop.first` as active by default — but if the
       first qty isn't 1, we still want the form to match). */
    const seed =
      Array.from(btns).find((b) => b.classList.contains('mps-ipd__qty-btn--active')) ||
      btns[0];
    if (qtyInput) qtyInput.value = seed.dataset.qty || '1';

    btns.forEach((btn) => {
      btn.addEventListener('click', () => {
        btns.forEach((b) => {
          b.classList.remove('mps-ipd__qty-btn--active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('mps-ipd__qty-btn--active');
        btn.setAttribute('aria-checked', 'true');
        if (qtyInput) qtyInput.value = btn.dataset.qty || '1';
      });
    });
  }

  /* ---- Close on successful cart add ----
     The CTA bar has x-data="xProductCart" and the button uses Alpine
     bindings (@click.prevent="addToCart($event)" + :disabled="loading" +
     :class="{'is-loading': loading}") — same wiring as the main page ATC.
     Alpine handles loading state, error state, section refresh, and cart
     drawer open. We just need to dismiss our own drawer once the pipeline
     fires its success event. */
  _bindCartChangeClose() {
    document.addEventListener('eurus:cart:items-changed', () => {
      if (this.classList.contains('is-open')) this.close();
    });
  }
}

if (!customElements.get('mps-included-drawer')) {
  customElements.define('mps-included-drawer', MpsIncludedDrawer);
}
